// Unified Analytics Utility
// Consolidates Google Ads and GA4 tracking with comprehensive error handling and PII protection

import { getSignupAttribution } from './signupAttribution';
import { getCookieConsent } from '../components/cookieConsent/cookieConsentStorage';

// Configuration
const CONFIG = {
  GA4_ID: 'G-N09QR4GQB3',
  GOOGLE_ADS_CONVERSION_ID: 'AW-17444411325/ZwOrCOrh5oQbEL2vkv5A',
  DEDUPLICATION_WINDOW: 5000 // 5 seconds
};

// De-duplication tracking
const eventTracker = new Map();

// PII protection - fields that should never be sent
const PII_FIELDS = ['email', 'name', 'whatsapp', 'phone', 'cpf', 'cnpj', 'password'];

/**
 * Sanitizes parameters to remove PII data
 * @param {Object} params - Parameters to sanitize
 * @returns {Object} - Sanitized parameters
 */
const sanitizeParams = (params) => {
  if (!params || typeof params !== 'object') return params;
  
  const sanitized = { ...params };
  
  PII_FIELDS.forEach(field => {
    if (sanitized[field]) {
      delete sanitized[field];
    }
  });
  
  return sanitized;
};

/**
 * Checks if an event should be deduplicated
 * @param {string} eventKey - Unique key for the event
 * @returns {boolean} - True if event should be skipped
 */
const shouldDeduplicate = (eventKey) => {
  const now = Date.now();
  const lastTracked = eventTracker.get(eventKey);
  
  if (lastTracked && (now - lastTracked) < CONFIG.DEDUPLICATION_WINDOW) {
    return true;
  }
  
  eventTracker.set(eventKey, now);
  return false;
};

/**
 * Checks if gtag is available
 * @returns {boolean} - True if gtag is available
 */
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && window.gtag && typeof window.gtag === 'function';
};

/**
 * GA4 só registra no site de produção. Usa a mesma regra do index.html
 * (window.gabrielaIsProductionHost, decidida pelo endereço): no local e em QA
 * os eventos GA4 não saem. A conversão do Google Ads não passa por aqui.
 * Sem o helper (testes unitários, que não carregam o index.html), segue liberado.
 * @returns {boolean}
 */
const isGa4Enabled = () => {
  if (typeof window === 'undefined') return false;
  if (typeof window.gabrielaIsProductionHost !== 'function') return true;
  return window.gabrielaIsProductionHost();
};

/**
 * Core function to dispatch events to GA4
 * @param {string} eventName - Name of the event
 * @param {Object} params - Event parameters
 */
export const gaEvent = (eventName, params = {}) => {
  try {
    if (!eventName || typeof eventName !== 'string') {
      return;
    }

    // Sanitize parameters to remove PII
    const sanitizedParams = sanitizeParams(params);
    
    // Create deduplication key
    const eventKey = `ga4_${eventName}_${JSON.stringify(sanitizedParams)}`;
    
    if (shouldDeduplicate(eventKey)) {
      return;
    }

    if (!isGa4Enabled()) {
      return;
    }

    if (!isGtagAvailable()) {
      return;
    }

    // Dispatch to GA4
    window.gtag('event', eventName, {
      ...sanitizedParams,
      send_to: CONFIG.GA4_ID
    });


  } catch {}
};

/**
 * Core function to dispatch conversions to Google Ads
 * @param {number} value - Conversion value
 * @param {string} currency - Currency code (default: BRL)
 */
export const adsConversion = (value = 1, currency = 'BRL') => {
  try {
    if (typeof value !== 'number' || value < 0) {
      return;
    }

    if (typeof currency !== 'string' || currency.length !== 3) {
      return;
    }

    // Create deduplication key
    const eventKey = `ads_conversion_${value}_${currency}`;
    
    if (shouldDeduplicate(eventKey)) {
      return;
    }

    if (!isGtagAvailable()) {
      return;
    }

    // Dispatch to Google Ads
    window.gtag('event', 'conversion', {
      send_to: CONFIG.GOOGLE_ADS_CONVERSION_ID,
      value: value,
      currency: currency
    });


  } catch {}
};

// Guarda de deduplicação específica para a conversão de cadastro do Google Ads
const firedSignupConversions = new Set();

/**
 * Dispara a conversão de cadastro do Google Ads.
 * Deve ser chamada somente após o servidor confirmar o cadastro (response.success),
 * e no máximo uma vez por cadastro (controlado por options.signupKey).
 * Não envia value/currency nem qualquer dado pessoal (PII) — apenas o evento de conversão.
 * @param {Object} [options] - Opções da conversão
 * @param {string} [options.signupKey] - Chave única do cadastro para deduplicação (default: 'signup')
 * @param {string} [options.transactionId] - Id do cadastro: o Google conta uma vez por id
 * @param {Function} [options.eventCallback] - Chamado pelo gtag.js quando o envio sai
 * @returns {boolean} - True se a conversão foi disparada com sucesso, false caso contrário
 */
export const trackGoogleAdsSignupConversion = (options = {}) => {
  const { signupKey = 'signup', transactionId, eventCallback } = options || {};

  if (!isGtagAvailable()) {
    return false;
  }

  if (firedSignupConversions.has(signupKey)) {
    return false;
  }

  // Adiciona a chave ANTES de chamar o gtag para evitar disparo duplicado em reentrância síncrona
  firedSignupConversions.add(signupKey);

  try {
    const conversionParams = { send_to: CONFIG.GOOGLE_ADS_CONVERSION_ID };
    if (transactionId) {
      conversionParams.transaction_id = transactionId;
    }
    if (eventCallback) {
      conversionParams.event_callback = eventCallback;
    }
    window.gtag('event', 'conversion', conversionParams);


    return true;
  } catch (error) {
    return false;
  }
};

// Ids de cadastro cuja conversão este navegador já disparou (entre abas e recargas)
const CONFIRMED_SIGNUP_STORAGE_KEY = 'gabriela_ads_signup_conversions';
// Espera máxima pelo envio antes de seguir a tela (a seguinte recarrega a página)
export const CONFIRMED_SIGNUP_SEND_TIMEOUT_MS = 1500;

const readFiredConfirmedSignups = () => {
  try {
    const list = JSON.parse(localStorage.getItem(CONFIRMED_SIGNUP_STORAGE_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
};

// Cookies do Google tag com o clique do anúncio: _gcl_aw (gclid), _gcl_gb (gbraid/wbraid)
const GOOGLE_ADS_CLICK_COOKIES = ['_gcl_aw', '_gcl_gb'];
const GOOGLE_ADS_CLICK_PARAMS = ['gclid', 'gbraid', 'wbraid'];

/**
 * Este navegador veio de um clique no Google Ads? Vale o cookie do clique gravado pelo
 * Google tag ou a origem do cadastro guardada aqui (signupAttribution) com gclid,
 * gbraid ou wbraid. Sem isso a conversão sairia sem clique e gastaria o transaction_id.
 * @returns {boolean}
 */
const hasGoogleAdsClick = () => {
  try {
    const cookies = typeof document !== 'undefined' ? document.cookie.split(';') : [];
    if (cookies.some((c) => GOOGLE_ADS_CLICK_COOKIES.includes(c.split('=')[0].trim()))) {
      return true;
    }
    const attribution = getSignupAttribution();
    return !!attribution && [attribution.first, attribution.last].some(
      (touch) => touch && GOOGLE_ADS_CLICK_PARAMS.some((key) => touch[key])
    );
  } catch (e) {
    return false;
  }
};

/**
 * Conversão do Google Ads no cadastro confirmado (e-mail confirmado = conta criada).
 * Chamada pela tela "Confirme seu e-mail", que fica no navegador do clique no
 * anúncio, e pela página do link do e-mail, para quem fechou aquela aba. As duas
 * mandam o mesmo conversionId (EmailVerificationService::signupConversionId) como
 * transaction_id, então o Google conta uma vez só; o localStorage evita o segundo
 * disparo no mesmo navegador.
 *
 * Com aceite dos cookies, só dispara num navegador com clique do Google Ads
 * (hasGoogleAdsClick). A confirmação em outro aparelho não manda nada: um envio sem
 * clique com o mesmo transaction_id poderia barrar o da aba do cadastro, que tem o
 * clique. Sem aceite (banner ignorado ou "Somente necessários") o Google tag roda em
 * Consent Mode negado e não há cookie de clique: a conversão sai sempre e o Google
 * estima a atribuição. Cadastro e confirmação seguem registrados no backend.
 *
 * Logo depois a tela entra na conta com recarga de página, que pode cortar o envio:
 * antes de sair, a tela passa a saída por afterConfirmedSignupConversion.
 * @param {string} conversionId - Id devolvido pelo backend na confirmação
 * @returns {boolean} - True se disparou agora
 */
export const trackGoogleAdsConfirmedSignupConversion = (conversionId) => {
  if (!conversionId || typeof conversionId !== 'string') {
    return false;
  }

  const fired = readFiredConfirmedSignups();
  if (fired.includes(conversionId)) {
    return false;
  }

  // Google tag rodando (produção, fora das rotas de paciente), com ou sem aceite
  const googleTagLoaded = typeof window !== 'undefined' && window.__gabrielaGoogleTagLoaded === true;
  // Sem aceite (Consent Mode negado) não há cookie para provar o clique: o envio é um
  // sinal sem identificação e o Google estima a quem atribuir. Com aceite vale a prova.
  const consentDenied = getCookieConsent() !== 'all';
  if (!hasGoogleAdsClick() && !(consentDenied && googleTagLoaded)) {
    return false;
  }

  // Sem o Google tag carregado (fora da produção) o gtag.js nunca avisa o envio:
  // não há o que esperar
  const waitForSend = googleTagLoaded;
  if (waitForSend) {
    confirmedSignupSendPending = true;
    setTimeout(finishConfirmedSignupSend, CONFIRMED_SIGNUP_SEND_TIMEOUT_MS);
  }
  const sent = trackGoogleAdsSignupConversion({
    signupKey: conversionId,
    transactionId: conversionId,
    eventCallback: waitForSend ? finishConfirmedSignupSend : undefined
  });
  if (!sent) {
    finishConfirmedSignupSend();
  }
  if (sent) {
    try {
      localStorage.setItem(CONFIRMED_SIGNUP_STORAGE_KEY, JSON.stringify([...fired, conversionId].slice(-20)));
    } catch (e) {
      // localStorage indisponível - fica a guarda em memória e o transaction_id
    }
  }
  return sent;
};

// Envio da conversão de cadastro confirmado ainda em curso, e quem espera por ele
let confirmedSignupSendPending = false;
let confirmedSignupSendWaiters = [];

function finishConfirmedSignupSend() {
  if (!confirmedSignupSendPending && confirmedSignupSendWaiters.length === 0) return;
  confirmedSignupSendPending = false;
  const waiters = confirmedSignupSendWaiters;
  confirmedSignupSendWaiters = [];
  waiters.forEach((waiter) => waiter());
}

/**
 * Roda next quando a conversão de cadastro confirmado já saiu: na hora, se não há
 * envio em curso; senão quando o gtag.js avisa o envio (event_callback) ou depois de
 * CONFIRMED_SIGNUP_SEND_TIMEOUT_MS, o que vier primeiro. Usado antes da recarga de
 * página que leva à conta.
 * @param {Function} next
 */
export const afterConfirmedSignupConversion = (next) => {
  if (typeof next !== 'function') return;
  if (!confirmedSignupSendPending) {
    next();
    return;
  }
  confirmedSignupSendWaiters.push(next);
};

/**
 * Limpa a guarda de deduplicação da conversão de cadastro.
 * Uso exclusivo em testes.
 */
export const resetSignupConversionGuard = () => {
  firedSignupConversions.clear();
  confirmedSignupSendPending = false;
  confirmedSignupSendWaiters = [];
};

/**
 * Helper function to track form submissions
 * Dispatches to both GA4 and Google Ads when successful
 * @param {Object} options - Form tracking options
 * @param {string} options.formName - Name of the form
 * @param {boolean} options.success - Whether the form submission was successful
 * @param {string} options.source - Source of the form submission
 */
export const trackFormSubmit = ({ formName, success = false, source = 'unknown' }) => {
  try {
    if (!formName || typeof formName !== 'string') {
      return;
    }

    // Sanitize parameters
    const params = sanitizeParams({
      form_name: formName,
      success: success,
      source: source
    });

    // Always track form submission in GA4
    gaEvent('form_submit', params);

    // Conversão Google Ads somente em sucesso confirmado, pelo caminho único (com guarda de duplicidade)
    if (success) {
      trackGoogleAdsSignupConversion();
    }


  } catch {}
};

/**
 * Helper function to track trial start events in GA4
 * @deprecated Use trackStartTrial() instead for better parameter validation and standardized tracking
 * @param {Object} options - Trial tracking options
 * @param {string} options.method - Method used to start the trial
 */
export const trackTrialStart = ({ method = 'unknown' } = {}) => {
  try {

    // Redirect to new standardized function with default plan for backward compatibility
    trackStartTrial({
      plan: 'legacy_trial',
      method: method,
      billingCycle: 'monthly'
    });

  } catch {}
};

// === PHASE 1: FORM CAPTURE EVENTS ===
// Funções para capturar eventos de interação no formulário da landing page

/**
 * Rastreia cliques no CTA "Quero começar meu teste grátis"
 * @param {Object} options - Opções de rastreamento do CTA
 * @param {string} options.placement - Local onde o CTA foi clicado (default: 'early_access_section')
 */
export function trackCtaClick({ placement = 'early_access_section' } = {}) {
  try {
    if (typeof placement !== 'string' || !placement.trim()) {
      return;
    }

    // Sanitiza parâmetros
    const params = sanitizeParams({
      placement: placement.trim()
    });

    gaEvent('cta_click', params);


  } catch {}
}

/**
 * Rastreia o início do preenchimento do formulário
 * @param {Object} options - Opções de rastreamento do formulário
 * @param {string} options.formName - Nome do formulário (default: 'early_access')
 */
export function trackFormStart({ formName = 'early_access' } = {}) {
  try {
    if (typeof formName !== 'string' || !formName.trim()) {
      return;
    }

    // Sanitiza parâmetros
    const params = sanitizeParams({
      form_name: formName.trim()
    });

    gaEvent('form_start', params);


  } catch {}
}

/**
 * Rastreia erros de validação no formulário
 * @param {Object} options - Opções de rastreamento de erro
 * @param {string} options.formName - Nome do formulário (default: 'early_access')
 * @param {string} options.field - Campo que gerou o erro
 * @param {string} options.errorCode - Código do erro de validação
 */
export function trackFormError({ formName = 'early_access', field, errorCode }) {
  try {
    if (typeof formName !== 'string' || !formName.trim()) {
      return;
    }

    if (!field || typeof field !== 'string' || !field.trim()) {
      return;
    }

    if (!errorCode || typeof errorCode !== 'string' || !errorCode.trim()) {
      return;
    }

    // Sanitiza parâmetros
    const params = sanitizeParams({
      form_name: formName.trim(),
      field: field.trim(),
      error_code: errorCode.trim()
    });

    gaEvent('form_error', params);


  } catch {}
}

// === PHASE 2: USER JOURNEY EVENTS ===

/**
 * Rastreia validação bem-sucedida da chave de early access (tela de inscrição)
 * @param {Object} options - Opções de rastreamento do código
 * @param {string} options.codeType - Tipo do código validado (default: 'early_access')
 */
export function trackCodeRedeemed({ codeType = 'early_access' } = {}) {
  try {
    if (typeof codeType !== 'string' || !codeType.trim()) {
      return;
    }

    // Sanitiza parâmetros
    const params = sanitizeParams({
      method: 'access_key',
      code_type: codeType.trim(),
      success: true
    });

    gaEvent('code_redeemed', params);


  } catch {}
}

/**
 * Rastreia cadastro da empresa/conta concluído (sem plano ainda)
 * @param {Object} options - Opções de rastreamento do cadastro
 * @param {string} options.method - Método usado para o cadastro (default: 'website')
 */
export function trackSignUp({ method = 'website' } = {}) {
  try {
    if (typeof method !== 'string' || !method.trim()) {
      return;
    }

    // Sanitiza parâmetros
    const params = sanitizeParams({
      method: method.trim()
    });

    gaEvent('sign_up', params);


  } catch {}
}

/**
 * Rastreia início de trial com validação rigorosa (substitui trackTrialStart legado)
 * @param {Object} options - Opções de rastreamento do trial
 * @param {string} options.plan - Plano selecionado (obrigatório)
 * @param {string} options.billingCycle - Ciclo de cobrança (default: 'monthly')
 * @param {string} options.method - Método usado para iniciar o trial (default: 'checkout')
 */
export function trackStartTrial({ plan, billingCycle = 'monthly', method = 'checkout' } = {}) {
  try {
    // Validação rigorosa do parâmetro obrigatório
    if (!plan || typeof plan !== 'string' || !plan.trim()) {
      return;
    }

    if (typeof billingCycle !== 'string' || !billingCycle.trim()) {
      return;
    }

    if (typeof method !== 'string' || !method.trim()) {
      return;
    }

    // Sanitiza parâmetros
    const params = sanitizeParams({
      plan: plan.trim(),
      billing_cycle: billingCycle.trim(),
      method: method.trim(),
      value: 0,
      currency: 'BRL'
    });

    gaEvent('start_trial', params);


  } catch {}
}

/**
 * Rastreia cancelamento pelo usuário no front
 * @param {Object} options - Opções de rastreamento do cancelamento
 * @param {string} options.plan - Plano que foi cancelado
 * @param {string} options.reason - Motivo do cancelamento (default: 'user_requested')
 */
export function trackUnsubscribe({ plan, reason = 'user_requested' } = {}) {
  try {
    if (typeof reason !== 'string' || !reason.trim()) {
      return;
    }

    // Sanitiza parâmetros
    const params = sanitizeParams({
      plan: plan ? plan.trim() : undefined,
      reason: reason.trim()
    });

    gaEvent('unsubscribe', params);


  } catch {}
}

// === EMAIL VERIFICATION & ONBOARDING EVENTS ===

/**
 * Rastreia o início do fluxo de cadastro (primeira interação com o formulário)
 * @param {Object} options - Opções de rastreamento
 * @param {string} options.method - Método/origem do cadastro (default: 'website')
 */
export function trackSignupStarted({ method = 'website' } = {}) {
  try {
    const params = sanitizeParams({
      method: typeof method === 'string' && method.trim() ? method.trim() : 'website'
    });

    gaEvent('signup_started', params);

  } catch {}
}

/**
 * Rastreia o envio do formulário de cadastro
 * @param {Object} options - Opções de rastreamento
 * @param {string} options.method - Método/origem do cadastro (default: 'website')
 */
export function trackSignupSubmitted({ method = 'website' } = {}) {
  try {
    const params = sanitizeParams({
      method: typeof method === 'string' && method.trim() ? method.trim() : 'website'
    });

    gaEvent('signup_submitted', params);

  } catch {}
}

/**
 * Rastreia o envio do e-mail de confirmação de cadastro
 * @param {Object} options - Opções de rastreamento
 * @param {string} options.method - Método/origem do cadastro (default: 'website')
 */
export function trackVerificationEmailSent({ method = 'website' } = {}) {
  try {
    const params = sanitizeParams({
      method: typeof method === 'string' && method.trim() ? method.trim() : 'website'
    });

    gaEvent('verification_email_sent', params);

  } catch {}
}

/**
 * Rastreia a confirmação bem-sucedida do e-mail de cadastro
 */
export function trackVerificationCompleted() {
  try {
    gaEvent('verification_completed', {});

  } catch {}
}

/**
 * Rastreia falha na confirmação do e-mail (token inválido/expirado/usado)
 * @param {Object} options - Opções de rastreamento
 * @param {string} options.reason - Motivo da falha (código de erro do backend, sem PII)
 */
export function trackVerificationFailed({ reason = 'unknown' } = {}) {
  try {
    const params = sanitizeParams({
      reason: typeof reason === 'string' && reason.trim() ? reason.trim() : 'unknown'
    });

    gaEvent('verification_failed', params);

  } catch {}
}

/**
 * Rastreia o reenvio do e-mail de confirmação
 */
export function trackVerificationResent() {
  try {
    gaEvent('verification_resent', {});

  } catch {}
}

/**
 * Rastreia o primeiro login realizado logo após a confirmação do e-mail
 */
export function trackFirstLoginAfterVerification() {
  try {
    gaEvent('first_login_after_verification', {});

  } catch {}
}

/**
 * Rastreia o início do onboarding (tela de boas-vindas do dono da empresa)
 */
export function trackOnboardingStarted() {
  try {
    gaEvent('onboarding_started', {});

  } catch {}
}

/**
 * Rastreia a criação do primeiro paciente da empresa
 */
export function trackFirstPatientCreated() {
  try {
    gaEvent('first_patient_created', {});

  } catch {}
}

/**
 * Rastreia a criação do primeiro agendamento da empresa
 */
export function trackFirstAppointmentCreated() {
  try {
    gaEvent('first_appointment_created', {});

  } catch {}
}

/**
 * Helper interno: dispara first_patient_created/first_appointment_created no máximo
 * uma vez por empresa, usando uma flag persistida em localStorage como guarda.
 * Trade-off: a guarda é local ao navegador/dispositivo (não sincronizada no backend),
 * então em tese poderia disparar novamente em outro navegador/dispositivo caso o
 * primeiro registro daquele tipo já tenha sido criado por lá. Aceitável para uma
 * métrica de produto (não é usada para faturamento nem decisões críticas).
 * @param {'patient'|'appointment'} entity - Tipo de entidade criada
 * @param {string|number} companyId - Identificador da empresa
 * @param {boolean} wasEmptyBeforeCreate - Se a lista estava vazia antes desta criação
 */
export function trackFirstEntityCreatedOnce(entity, companyId, wasEmptyBeforeCreate) {
  try {
    if (!entity || (entity !== 'patient' && entity !== 'appointment')) {
      return;
    }
    if (!wasEmptyBeforeCreate) {
      return;
    }
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    const flagKey = `first_${entity}_tracked_${companyId || 'unknown'}`;

    if (window.localStorage.getItem(flagKey)) {
      return;
    }

    window.localStorage.setItem(flagKey, '1');

    if (entity === 'patient') {
      trackFirstPatientCreated();
    } else {
      trackFirstAppointmentCreated();
    }
  } catch {}
}

// Backward compatibility functions
// These maintain the same signatures as the original googleAds.js

/**
 * Legacy function for page load tracking (backward compatibility)
 * @deprecated Use gaEvent('page_view', params) instead
 */
export const trackPageLoad = () => {
  try {
    gaEvent('page_view', {
      page_title: typeof document !== 'undefined' ? document.title : 'Unknown',
      page_location: typeof window !== 'undefined' ? window.location.href : 'Unknown'
    });
  } catch {}
};

/**
 * Legacy function for conversion tracking (backward compatibility)
 * @deprecated Use trackGoogleAdsSignupConversion() instead
 */
export const trackConversion = () => {
  try {
    trackGoogleAdsSignupConversion();
  } catch {}
};

// Utility functions for debugging and maintenance

/**
 * Get current configuration
 * @returns {Object} - Current configuration
 */
export const getConfig = () => ({ ...CONFIG });

/**
 * Update configuration
 * @param {Object} newConfig - New configuration values
 */
export const updateConfig = (newConfig) => {
  if (newConfig && typeof newConfig === 'object') {
    Object.assign(CONFIG, newConfig);
  }
};

/**
 * Clear event deduplication cache
 */
export const clearEventCache = () => {
  eventTracker.clear();
};

/**
 * Get analytics status
 * @returns {Object} - Status information
 */
export const getAnalyticsStatus = () => {
  return {
    gtagAvailable: isGtagAvailable(),
    cacheSize: eventTracker.size,
    config: getConfig()
  };
};

// Export configuration for external access
export { CONFIG };